import { EbookTemplate } from '../../../mocks/ebookTemplates';

interface Props {
  template: EbookTemplate;
  onClick: () => void;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
  isInCompare: boolean;
  onToggleCompare: (t: EbookTemplate) => void;
  compareCount: number;
  onFullScreenPreview: (t: EbookTemplate) => void;
  onShare: (t: EbookTemplate) => void;
}

const MAX_COMPARE = 3;

const BADGE_STYLES: Record<string, string> = {
  Popular: 'bg-amber-100 text-amber-700 border border-amber-200',
  New: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  Trending: 'bg-rose-100 text-rose-700 border border-rose-200',
};

const TAG_COLORS: Record<string, string> = {
  Bold: 'bg-slate-100 text-slate-700',
  Dark: 'bg-slate-800/10 text-slate-700',
  Minimal: 'bg-slate-100 text-slate-600',
  Colorful: 'bg-pink-50 text-pink-600',
  Elegant: 'bg-amber-50 text-amber-600',
  Modern: 'bg-sky-50 text-sky-600',
  Corporate: 'bg-indigo-50 text-indigo-600',
  Creative: 'bg-violet-50 text-violet-600',
  Clean: 'bg-emerald-50 text-emerald-600',
  Warm: 'bg-orange-50 text-orange-600',
  Academic: 'bg-cyan-50 text-cyan-700',
  Inspiring: 'bg-rose-50 text-rose-600',
};

export default function EbookTemplateListCard({
  template, onClick, isFavorite, onToggleFavorite,
  isInCompare, onToggleCompare, compareCount, onFullScreenPreview, onShare,
}: Props) {
  const canAddToCompare = isInCompare || compareCount < MAX_COMPARE;
  const readTime = Math.round(template.pages / 25);

  return (
    <div
      className={`group bg-white rounded-2xl border-2 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden cursor-pointer flex items-stretch ${
        isInCompare ? 'border-amber-400 shadow-md shadow-amber-100' : 'border-slate-200 hover:border-amber-300'
      }`}
      onClick={onClick}
    >
      {/* ── Thumbnail ──────────────────────────────────────────────────────── */}
      <div className="relative flex-shrink-0 w-[72px] overflow-hidden">
        <img
          src={template.image}
          alt={template.name}
          className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10" />
        <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: template.color }}></div>
        {isInCompare && <div className="absolute top-0 left-0 right-0 h-0.5 bg-amber-400"></div>}
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex items-center gap-4 px-4 py-3">
        {/* Title + category + desc */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-sm font-extrabold text-slate-900 truncate group-hover:text-amber-700 transition-colors">
              {template.name}
            </h3>
            {template.badge && (
              <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold ${BADGE_STYLES[template.badge]}`}>
                {template.badge}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-400 mb-1.5 truncate">{template.category}</p>
          <p className="text-xs text-slate-500 line-clamp-1 leading-relaxed hidden sm:block">{template.description}</p>
        </div>

        {/* Tags */}
        <div className="hidden md:flex items-center gap-1.5 flex-shrink-0">
          {template.tags.slice(0, 2).map(tag => (
            <span
              key={tag}
              className={`px-2 py-0.5 rounded-lg text-xs font-semibold whitespace-nowrap ${TAG_COLORS[tag] || 'bg-slate-100 text-slate-500'}`}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Stats */}
        <div className="hidden lg:flex items-center gap-4 text-xs text-slate-500 flex-shrink-0">
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-amber-400"></i>
            <strong className="text-slate-700">{template.rating}</strong>
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-book-open-line text-slate-400"></i>
            {template.chapters} ch.
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-pages-line text-slate-400"></i>
            {template.pages}p
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line text-slate-400"></i>
            ~{readTime}h
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-download-line text-slate-400"></i>
            {(template.downloads / 1000).toFixed(1)}k
          </span>
        </div>

        {/* Color swatch */}
        <div className="flex-shrink-0 hidden xl:block">
          <div
            className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
            style={{ backgroundColor: template.color }}
            title={template.color}
          ></div>
        </div>
      </div>

      {/* ── Actions ────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1.5 pr-3 flex-shrink-0" onClick={e => e.stopPropagation()}>
        {/* Full Screen Preview */}
        <button
          onClick={() => onFullScreenPreview(template)}
          title="Full screen preview"
          className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 text-slate-400 hover:border-amber-300 hover:text-amber-600 transition-all cursor-pointer"
        >
          <i className="ri-fullscreen-line text-sm"></i>
        </button>

        {/* Share */}
        <button
          onClick={() => onShare(template)}
          title="Share template"
          className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 text-slate-400 hover:border-amber-300 hover:text-amber-600 transition-all cursor-pointer"
        >
          <i className="ri-share-line text-sm"></i>
        </button>

        {/* Compare */}
        <button
          onClick={() => onToggleCompare(template)}
          disabled={!canAddToCompare}
          title={isInCompare ? 'Remove from compare' : 'Add to compare'}
          className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all cursor-pointer ${
            isInCompare
              ? 'bg-amber-500 border-amber-500 text-white'
              : canAddToCompare
              ? 'border-slate-200 text-slate-400 hover:border-amber-300 hover:text-amber-600'
              : 'border-slate-200 text-slate-300 cursor-not-allowed'
          }`}
        >
          <i className={`${isInCompare ? 'ri-check-line' : 'ri-layout-column-line'} text-sm`}></i>
        </button>

        {/* Favorite */}
        <button
          onClick={() => onToggleFavorite(template.id)}
          className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all cursor-pointer ${
            isFavorite
              ? 'bg-rose-500 border-rose-500 text-white'
              : 'border-slate-200 text-slate-400 hover:border-rose-300 hover:text-rose-500'
          }`}
        >
          <i className={`${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
        </button>

        {/* Use Template */}
        <button
          onClick={() => onClick()}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white transition-all cursor-pointer whitespace-nowrap hover:opacity-90"
          style={{ backgroundColor: template.color }}
        >
          <i className="ri-edit-line text-xs"></i>
          Use
        </button>
      </div>
    </div>
  );
}
